// Geçmiş Rapor Aktarımı — toplu aktarım ekranı (Task 13).
//
// Route `/projects/import` (RG7 — brief'teki `/admin/projects/import` geçersiz);
// dosya yolu brief'teki gibi `pages/admin/` altında kalır (izin `projects.import_report`).
// Üç sekme `?tab=new|jobs|done` (deep-link, FormManager tablist stili); sayfa sol
// dayalı `p-8` + `maxWidth: 1400` (mx-auto YOK — feedback_page_layout_left_aligned).

import { useCallback, useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useAuthStore } from "../../stores/auth-store";
import { fillTemplate } from "../../lib/period-ui";
import { IMPORT_COPY } from "../../lib/report-import-ui";
import { NewBatchTab } from "../../components/report-import/NewBatchTab";
import { JobsTab } from "../../components/report-import/JobsTab";

type TabKey = "new" | "jobs" | "done";

const TABS: { key: TabKey; label: string }[] = [
  { key: "new", label: IMPORT_COPY.tabNew },
  { key: "jobs", label: IMPORT_COPY.tabJobs },
  { key: "done", label: IMPORT_COPY.tabDone },
];

/** TransferMenu deseni — başarı bandı 6 sn görünür. */
const BAND_MS = 6000;

export function ReportImport(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const [params, setParams] = useSearchParams();
  const raw = params.get("tab");
  const tab: TabKey = raw === "jobs" || raw === "done" ? raw : "new";
  const [band, setBand] = useState<string | null>(null);

  const setTab = useCallback(
    (next: TabKey): void => {
      const p = new URLSearchParams(params);
      p.set("tab", next);
      setParams(p, { replace: true });
    },
    [params, setParams],
  );

  useEffect(() => {
    if (!band) return;
    const t = window.setTimeout(() => setBand(null), BAND_MS);
    return () => window.clearTimeout(t);
  }, [band]);

  const onBatchCreated = useCallback(
    (count: number): void => {
      setBand(fillTemplate(IMPORT_COPY.batchQueued, { n: count }));
      setTab("jobs");
    },
    [setTab],
  );

  const canImport = user?.permissions["projects.import_report"] === true;
  if (user && !canImport) return <Navigate to="/projects" replace />;

  return (
    <div className="p-8" style={{ fontFamily: "var(--font-sans)", maxWidth: 1400 }}>
      <header className="mb-6">
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "0.72rem",
            letterSpacing: "0.08em",
            color: "var(--color-text-2)",
            textTransform: "uppercase",
            marginBottom: 4,
          }}
        >
          {IMPORT_COPY.pageEyebrow}
        </div>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "1.8rem",
            color: "var(--color-text)",
          }}
        >
          {IMPORT_COPY.pageTitle}
        </h1>
        {/* Alt başlık YOK — `dropzoneFirstUse` satırı sekmenin kendi boş durumunda
            duruyor (8A); burada tekrarlamak aynı cümleyi iki kez gösterirdi. */}
      </header>

      {band ? (
        <div
          role="status"
          className="mb-4"
          style={{
            padding: "10px 14px",
            borderRadius: 8,
            fontSize: "0.88rem",
            background: "color-mix(in srgb, var(--color-success) 10%, white)",
            border: "1px solid color-mix(in srgb, var(--color-success) 35%, white)",
            color: "var(--color-text-2)",
          }}
        >
          {band}
        </div>
      ) : null}

      <div
        role="tablist"
        aria-label={IMPORT_COPY.pageTitle}
        style={{ display: "flex", gap: 4, marginBottom: 16 }}
      >
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: "7px 16px",
              fontSize: "0.9rem",
              fontWeight: tab === t.key ? 600 : 400,
              background: tab === t.key ? "var(--color-primary)" : "transparent",
              color: tab === t.key ? "var(--color-surface)" : "var(--color-text-2)",
              border: "1px solid var(--color-border)",
              borderRadius: 8,
              cursor: "pointer",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "new" ? (
        <NewBatchTab onBatchCreated={onBatchCreated} />
      ) : (
        <JobsTab
          key={tab}
          finished={tab === "done"}
          canOpenReport={canImport}
          onGoToNew={() => setTab("new")}
        />
      )}
    </div>
  );
}
